"use client";
import {startBiteLogin} from '@/lib/bite-signup-referral';

import { useCallback, useEffect, useState,useRef } from "react";
import {useBiteScreen} from '@/components/bite/use-bite-screen';
import {trackBiteEvent} from '@/lib/bite-measurement';
import {checkoutFlowDetails} from '@/lib/bite-conversion';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X } from 'lucide-react';
import { BiteButton } from '@/components/bite/bite-ui';

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnTo?: string;
  variant?: 'default' | 'bite';
  error?: string;
}

export function LoginModal({ open, onOpenChange, returnTo, variant='default', error }: LoginModalProps) {
  const [apple,setApple]=useState(false);
  const [recentProvider,setRecentProvider]=useState<string|null>(null);
  useEffect(()=>{if(open){const value=document.cookie.split(';').map(v=>v.trim()).find(v=>v.startsWith('bite_recent_login='))?.split('=')[1];setRecentProvider(value==='google'||value==='apple'?value:null);}},[open]);
  const root=useRef<HTMLDivElement>(null);
  const [content,setContent]=useState<HTMLDivElement|null>(null);
  const captureContent=useCallback((element:HTMLDivElement|null)=>{root.current=element;setContent(element);},[]);
  const flowDetails=checkoutFlowDetails(returnTo||'');
  const checkoutLogin=variant==='bite'&&/(?:\?|&)plan=(?:monthly|card)(?:&|$)/.test(returnTo||'');
  useBiteScreen(root,open&&variant==='bite'&&content!==null,'login_view',`login-picker:${flowDetails.flow||returnTo}`,flowDetails);
  useEffect(()=>{if(open&&variant==='bite'&&error)trackBiteEvent('login_failed',checkoutFlowDetails(returnTo||''));},[open,variant,error,returnTo]);
  useEffect(()=>{
    if(!open||variant!=='bite')return;
    const controller=new AbortController();
    void api<{apple:boolean}>('/api/auth/providers',{signal:controller.signal}).then(value=>setApple(value.apple)).catch(()=>{});
    return()=>controller.abort();
  },[open,variant]);
  const handleLogin = useCallback((provider:'google'|'apple'='google') => {
    const next = returnTo || window.location.pathname;
    if(window.location.pathname.startsWith('/bite')||/^(www\.)?bite\.cx$/.test(window.location.hostname)) {
      void startBiteLogin(next,provider);return;
    }
    window.location.href = `/auth/google?next=${encodeURIComponent(next)}`;
  }, [returnTo]);
  const LoginButton=variant==='bite'?BiteButton:Button;

  return (
    <Dialog open={open} onOpenChange={value=>{if(!value&&variant==='bite')trackBiteEvent('login_dismiss',flowDetails);onOpenChange(value);}}>
      <DialogContent ref={captureContent} className={variant==='bite'?'bite-login-dialog bite-scope bite-light':undefined} overlayClassName={variant==='bite'?'bite-login-overlay bite-scope':undefined} showCloseButton={variant!=='bite'}>
        {variant==='bite'&&<DialogClose asChild><BiteButton type="button" variant="ghost" className="bite-login-close" aria-label="로그인 창 닫기" data-no-track><X aria-hidden="true"/></BiteButton></DialogClose>}
        <DialogHeader>
          {variant==='bite'?<div className="bite-login-brand"><img src="/bite-logo.png" alt="" width={40} height={40}/><span>Bite</span></div>:<div className="mx-auto mb-[var(--spacing-md)] flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[var(--color-accent-soft)]">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-[var(--color-accent)]"
            >
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
          </div>}
          <DialogTitle>{variant==='bite'?(checkoutLogin?'로그인하고 결제하기':'로그인하세요'):'로그인이 필요합니다'}</DialogTitle>
          <DialogDescription>
            {variant==='bite'?(checkoutLogin?'로그인하면 선택한 상품의 결제창으로 이동합니다.':'로그인하고 보고서를 확인하세요.'):'기업 분석 리포트를 생성하려면 Google 계정으로 로그인해주세요. 무료 사용자도 일부 기능을 이용할 수 있습니다.'}
          </DialogDescription>
        </DialogHeader>
        {error&&<p role="alert" className="bite-login-error">{error}</p>}

        <DialogFooter className="mt-[var(--spacing-lg)]">
          {variant!=='bite'&&<Button variant="ghost" onClick={() => onOpenChange(false)}>
            취소
          </Button>}
          <LoginButton type="button" onClick={()=>handleLogin('google')} data-track={variant==='bite'?'bite.login.google':undefined} className={variant==='bite'?'bite-login-provider bite-login-provider--google':undefined}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Google로 로그인
            {variant==='bite'&&recentProvider==='google'&&<span className="bite-recent-login">최근 로그인</span>}
          </LoginButton>
          {variant==='bite'&&apple&&<BiteButton type="button" onClick={()=>handleLogin('apple')} data-track="bite.login.apple" className="bite-login-provider bite-login-provider--apple">
            <svg width="18" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 12.04c.03 3.23 2.83 4.3 2.86 4.31-.02.08-.45 1.54-1.48 3.05-.9 1.31-1.83 2.62-3.3 2.65-1.45.03-1.92-.86-3.58-.86-1.65 0-2.17.83-3.55.89-1.42.05-2.5-1.42-3.4-2.72-1.85-2.67-3.26-7.55-1.36-10.84.95-1.63 2.64-2.67 4.47-2.7 1.4-.02 2.73.95 3.59.95.86 0 2.48-1.18 4.17-1.01.7.03 2.68.28 3.95 2.14-.1.06-2.36 1.37-2.33 4.14ZM14.38 3.99c.75-.91 1.26-2.17 1.12-3.43-1.08.04-2.38.72-3.15 1.63-.7.8-1.31 2.08-1.15 3.31 1.21.1 2.44-.62 3.18-1.51Z"/></svg>
            Apple로 로그인
            {recentProvider==='apple'&&<span className="bite-recent-login">최근 로그인</span>}
          </BiteButton>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
